import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { 
  Music, 
  Edit, 
  Trash2, 
  Loader2, 
  Search, 
  FileAudio,
  ExternalLink,
  Save,
  X,
  FolderOpen,
  Plus,
  Star,
  Download,
  FileText,
  ScrollText
} from "lucide-react";
import { LeadSheetAdminDialog } from "./LeadSheetAdminDialog";
import { toast } from "sonner";

export default function AdminContentManager() {
  const { token, loading: authLoading } = useAdminAuth();
  const [content, setContent] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  // Manual customer-fulfillment download dialog
  const [downloadDialog, setDownloadDialog] = useState(null); // { content_id, title }
  const [leadSheetItem, setLeadSheetItem] = useState(null);
  const [downloadEmail, setDownloadEmail] = useState("");
  const [downloadNote, setDownloadNote] = useState("");
  const [downloading, setDownloading] = useState(false);
  // Track which song is currently transcribing (contentId)
  const [transcribingId, setTranscribingId] = useState(null);
  const [transcribingAll, setTranscribingAll] = useState(false);

  const handleTranscribe = async (item) => {
    // Zero-click auto-publish: no confirm dialog. Lyrics go live immediately.
    const label = item.song_title || item.title;
    setTranscribingId(item.content_id);
    try {
      const res = await axios.post(
        `${API}/admin/content/${item.content_id}/transcribe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Transcribed "${label}" — ${res.data?.character_count || 0} characters saved.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed");
    } finally {
      setTranscribingId(null);
    }
  };

  const handleTranscribeAll = async () => {
    setTranscribingAll(true);
    try {
      const res = await axios.post(
        `${API}/admin/content/transcribe-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const queued = res.data?.queued || 0;
      if (queued === 0) {
        toast.info(res.data?.message || "Every song already has lyrics.");
      } else {
        toast.success(res.data?.message || `Queued ${queued} songs. Refreshing…`);
        // Poll every 15s so the UI reflects new transcripts as Whisper finishes them.
        const poll = setInterval(() => fetchData(), 15000);
        setTimeout(() => clearInterval(poll), 15 * 60 * 1000); // stop after 15 min
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Batch transcription failed");
    } finally {
      setTranscribingAll(false);
    }
  };

  const handleWipeExternalLyrics = async () => {
    if (!window.confirm("Wipe lyrics from every non-uploaded (external/demo) song? This cleans up transcripts that were mistakenly generated for demo tracks. Your real uploaded songs will keep their lyrics.")) return;
    try {
      const res = await axios.post(
        `${API}/admin/content/lyrics/wipe-external`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || `Wiped lyrics from ${res.data?.wiped || 0} songs.`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cleanup failed");
    }
  };

  const handleDeleteLyrics = async (item) => {
    const label = item.song_title || item.title;
    if (!window.confirm(`Delete lyrics for "${label}"? The green icon will return to amber and subscribers will stop seeing the transcript.`)) return;
    try {
      await axios.delete(
        `${API}/admin/content/${item.content_id}/lyrics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Removed lyrics for "${label}".`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const [newContent, setNewContent] = useState({
    title: "",
    description: "",
    course_name: "",
    category: "1L",
    vibe: "pop",
    file_url: "",
    song_title: ""
  });
  const [saving, setSaving] = useState(false);
  const [filterCourse, setFilterCourse] = useState("all");
  const [filterVibe, setFilterVibe] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [vibes, setVibes] = useState([]);

  // Convert string to Title Case
  const toTitleCase = (str) => {
    if (!str) return str;
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    if (token && !authLoading) {
      fetchData();
    }
  }, [token, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contentRes, filesRes, vibesRes, coursesRes] = await Promise.all([
        axios.get(`${API}/admin/content`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/files`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/vibes`),
        axios.get(`${API}/courses-v2`)
      ]);
      
      setContent(contentRes.data || []);
      setUploadedFiles(filesRes.data.files || []);
      setVibes(vibesRes.data.vibes || []);
      setCourses(coursesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch data", error);
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem({ ...item });
  };

  const handleSave = async () => {
    if (!editingItem) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/content/${editingItem.content_id}`,
        {
          title: editingItem.title,
          description: editingItem.description,
          file_url: editingItem.file_url,
          vibe: editingItem.vibe,
          course_name: editingItem.course_name,
          category: editingItem.category,
          duration: editingItem.duration,
          song_title: editingItem.song_title || ""
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Content updated successfully");
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update content");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (contentId, title) => {
    if (!confirm(`Are you sure you want to delete "${title}"?`)) return;
    
    try {
      await axios.delete(`${API}/admin/content/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Content deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete content");
    }
  };

  const openDownloadDialog = (item) => {
    setDownloadDialog({
      content_id: item.content_id,
      title: item.song_title || item.title,
    });
    setDownloadEmail("");
    setDownloadNote("");
  };

  const handleAdminDownload = async () => {
    if (!downloadDialog) return;
    const email = downloadEmail.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Enter a valid customer email");
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.post(
        `${API}/admin/song-download`,
        {
          content_id: downloadDialog.content_id,
          licensed_to: email,
          order_note: downloadNote.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );
      // Trigger browser download
      const blob = new Blob([res.data], { type: "audio/mpeg" });
      const safe = (downloadDialog.title || "barsongz").replace(/[^a-z0-9\-_ ]/gi, "_").trim() || "barsongz";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe}.mp3`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded "${downloadDialog.title}" — watermark = ${email}`);
      setDownloadDialog(null);
    } catch (error) {
      const err = error.response?.data;
      // If server returned a blob (error body), try to decode it
      let detail = "Download failed";
      if (err instanceof Blob) {
        try {
          const text = await err.text();
          detail = JSON.parse(text)?.detail || detail;
        } catch {
          detail = "Download failed";
        }
      } else {
        detail = err?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setDownloading(false);
    }
  };

  const handleSetAsSample = async (contentId, title) => {
    try {
      await axios.post(`${API}/admin/set-sample/${contentId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${title}" is now the free sample on the landing page!`);
      fetchData();
    } catch (error) {
      toast.error("Failed to set as sample");
    }
  };

  const handleAddContent = async () => {
    if (!newContent.title || !newContent.course_name || !newContent.vibe) {
      toast.error("Please fill in Title, Course, and Vibe");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("title", newContent.title);
      formData.append("course_name", newContent.course_name);
      formData.append("category", newContent.category);
      formData.append("vibe", newContent.vibe);
      formData.append("file_url", newContent.file_url || "");
      formData.append("description", newContent.description || "");
      formData.append("song_title", newContent.song_title || "");
      formData.append("duration", 0);

      await axios.post(`${API}/admin/create-content-from-upload`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`"${newContent.title}" added to library!`);
      setShowAddDialog(false);
      setNewContent({
        title: "",
        description: "",
        course_name: "",
        category: "1L",
        vibe: "pop",
        file_url: "",
        song_title: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add content");
    } finally {
      setSaving(false);
    }
  };

  const getFileStatus = (fileUrl) => {
    if (!fileUrl) return { status: "missing", label: "No URL" };
    if (fileUrl.includes("/api/uploads/")) return { status: "uploaded", label: "Your File" };
    if (fileUrl.includes("soundhelix") || fileUrl.includes("placeholder")) return { status: "demo", label: "Demo" };
    return { status: "external", label: "External" };
  };

  // Get unique courses from content
  const uniqueCourses = [...new Set(content.map(c => c.course_name).filter(Boolean))];
  const uniqueVibes = [...new Set(content.map(c => c.vibe).filter(Boolean))];

  // Filter content
  const filteredContent = content.filter(item => {
    const matchesCourse = filterCourse === "all" || item.course_name === filterCourse;
    const matchesVibe = filterVibe === "all" || item.vibe === filterVibe;
    const matchesSearch = !searchTerm || 
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.course_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCourse && matchesVibe && matchesSearch;
  });

  // Stats
  const stats = {
    total: content.length,
    withUploadedFiles: content.filter(c => c.file_url?.includes("/api/uploads/")).length,
    withDemoFiles: content.filter(c => c.file_url?.includes("soundhelix")).length,
    missing: content.filter(c => !c.file_url).length
  };

  const vibeColors = {
    pop: "bg-pink-500/20 text-pink-400",
    rock: "bg-red-500/20 text-red-400",
    rnb: "bg-purple-500/20 text-purple-400",
    country: "bg-amber-500/20 text-amber-400",
    latinx: "bg-orange-500/20 text-orange-400"
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-bold">Content Manager</h1>
          <p className="text-muted-foreground">Add, edit, and link audio files to library content</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleWipeExternalLyrics}
            variant="outline"
            className="border-red-400 text-red-600 hover:bg-red-500 hover:text-white"
            title="Delete lyrics that were mistakenly generated for demo/external songs"
            data-testid="wipe-external-lyrics-btn"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Wipe Demo Lyrics
          </Button>
          <Button
            onClick={handleTranscribeAll}
            disabled={transcribingAll}
            variant="outline"
            className="border-amber-500 text-amber-700 hover:bg-amber-500 hover:text-white"
            title="Auto-transcribe every uploaded song without lyrics via Whisper AI"
            data-testid="transcribe-all-btn"
          >
            {transcribingAll
              ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              : <FileText className="mr-2 h-4 w-4" />}
            Transcribe All Remaining
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="bg-gold text-primary-foreground">
            <Plus className="mr-2 h-4 w-4" />
            Add New Content
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-muted-foreground">Total Content</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-green-500">{stats.withUploadedFiles}</p>
            <p className="text-xs text-muted-foreground">With Your Audio</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-amber-500">{stats.withDemoFiles}</p>
            <p className="text-xs text-muted-foreground">Demo Audio</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-2xl font-bold text-red-500">{stats.missing}</p>
            <p className="text-xs text-muted-foreground">Missing Audio</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterCourse} onValueChange={setFilterCourse}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by course" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Courses</SelectItem>
                {uniqueCourses.map(course => (
                  <SelectItem key={course} value={course}>{course}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterVibe} onValueChange={setFilterVibe}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by vibe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Vibes</SelectItem>
                {uniqueVibes.map(vibe => (
                  <SelectItem key={vibe} value={vibe}>{vibe}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Content Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-gold" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Vibe</TableHead>
                  <TableHead>Audio Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContent.map((item) => {
                  const fileStatus = getFileStatus(item.file_url);
                  return (
                    <TableRow key={item.content_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.title}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {item.description || "No description"}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.course_name}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={vibeColors[item.vibe] || "bg-gray-500/20"}>
                          {item.vibe}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant="outline"
                          className={
                            fileStatus.status === "uploaded" ? "text-green-500 border-green-500" :
                            fileStatus.status === "demo" ? "text-amber-500 border-amber-500" :
                            fileStatus.status === "missing" ? "text-red-500 border-red-500" :
                            ""
                          }
                        >
                          <FileAudio className="mr-1 h-3 w-3" />
                          {fileStatus.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetAsSample(item.content_id, item.title)}
                            title="Set as landing page sample"
                            className={item.is_sample ? "text-gold" : "text-muted-foreground"}
                          >
                            <Star className={`h-4 w-4 ${item.is_sample ? "fill-gold" : ""}`} />
                          </Button>
                          {item.file_url && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                if (item.lyrics && e.shiftKey) {
                                  handleDeleteLyrics(item);
                                } else {
                                  handleTranscribe(item);
                                }
                              }}
                              disabled={transcribingId === item.content_id || transcribingAll}
                              title={item.lyrics ? "Re-transcribe (overwrites) — Shift+Click to DELETE lyrics" : "Transcribe song to lyrics via Whisper AI"}
                              className={item.lyrics ? "text-green-600 hover:text-green-800" : "text-amber-600 hover:text-amber-800"}
                              data-testid={`transcribe-btn-${item.content_id}`}
                            >
                              {transcribingId === item.content_id
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <FileText className="h-4 w-4" />}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setLeadSheetItem(item)}
                            title={item.lead_sheet ? "Lead Sheet attached: manage or unlock for students" : "Attach a Lead Sheet PDF"}
                            className={item.lead_sheet ? "text-green-600 hover:text-green-800" : "text-muted-foreground hover:text-primary"}
                            data-testid={`lead-sheet-admin-btn-${item.content_id}`}
                          >
                            <ScrollText className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDownloadDialog(item)}
                            title="Download watermarked MP3 for a customer"
                            className="text-purple-600 hover:text-purple-800"
                            data-testid={`download-for-customer-${item.content_id}`}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(item)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => handleDelete(item.content_id, item.title)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Content</DialogTitle>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Title</Label>
                  <Input
                    value={editingItem.title || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, title: toTitleCase(e.target.value) })}
                    placeholder="Enter title (auto-capitalizes)"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Course</Label>
                  <Select
                    value={editingItem.course_name || ""}
                    onValueChange={(v) => setEditingItem({ ...editingItem, course_name: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map(c => (
                        <SelectItem key={c.course_name} value={c.course_name}>
                          {c.course_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Vibe / Style</Label>
                  <Select
                    value={editingItem.vibe || ""}
                    onValueChange={(v) => setEditingItem({ ...editingItem, vibe: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {vibes.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={editingItem.category || ""}
                    onValueChange={(v) => setEditingItem({ ...editingItem, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1L">1L</SelectItem>
                      <SelectItem value="2L">2L</SelectItem>
                      <SelectItem value="3L">3L</SelectItem>
                      <SelectItem value="bar_prep">Bar Prep</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Input
                  value={editingItem.description || ""}
                  onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  Song Title
                </Label>
                <Input
                  value={editingItem.song_title || ""}
                  onChange={(e) => setEditingItem({ ...editingItem, song_title: e.target.value })}
                  placeholder="Display name for this audio track"
                />
              </div>

              {/* Audio File Selection */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Music className="h-4 w-4" />
                  Audio File URL
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={editingItem.file_url || ""}
                    onChange={(e) => setEditingItem({ ...editingItem, file_url: e.target.value })}
                    placeholder="Enter URL or select from uploaded files"
                    className="flex-1"
                  />
                  {editingItem.file_url && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(
                        editingItem.file_url.startsWith("/api") 
                          ? `${window.location.origin}${editingItem.file_url}`
                          : editingItem.file_url, 
                        "_blank"
                      )}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Uploaded Files Quick Select */}
              {uploadedFiles.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FolderOpen className="h-4 w-4" />
                    Quick Select from Uploaded Files
                  </Label>
                  <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                    {uploadedFiles.filter(f => !f.linked_content).map(file => (
                      <button
                        key={file.filename}
                        className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-muted transition-colors ${
                          editingItem.file_url === file.file_url ? "bg-gold/20 text-gold" : ""
                        }`}
                        onClick={() => setEditingItem({ ...editingItem, file_url: file.file_url })}
                      >
                        <span className="font-mono text-xs">{file.filename}</span>
                      </button>
                    ))}
                    {uploadedFiles.filter(f => !f.linked_content).length === 0 && (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No unlinked files. Upload more in Bulk Upload.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingItem(null)}>
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} className="bg-gold text-primary-foreground">
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-2 h-4 w-4" />
                  )}
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add New Content Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-gold" />
              Add New Content to Library
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={newContent.title}
                  onChange={(e) => setNewContent({ ...newContent, title: toTitleCase(e.target.value) })}
                  placeholder="e.g., Subject Matter Jurisdiction"
                />
              </div>
              <div className="space-y-2">
                <Label>Course *</Label>
                <Select
                  value={newContent.course_name}
                  onValueChange={(v) => setNewContent({ ...newContent, course_name: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select course" />
                  </SelectTrigger>
                  <SelectContent>
                    {courses.map(c => (
                      <SelectItem key={c.course_name} value={c.course_name}>
                        {c.course_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vibe / Style *</Label>
                <Select
                  value={newContent.vibe}
                  onValueChange={(v) => setNewContent({ ...newContent, vibe: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {vibes.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={newContent.category}
                  onValueChange={(v) => setNewContent({ ...newContent, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1L">1L</SelectItem>
                    <SelectItem value="2L">2L</SelectItem>
                    <SelectItem value="3L">3L</SelectItem>
                    <SelectItem value="bar_prep">Bar Prep</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newContent.description}
                onChange={(e) => setNewContent({ ...newContent, description: e.target.value })}
                placeholder="Brief description (optional)"
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Music className="h-4 w-4" />
                Song Title
              </Label>
              <Input
                value={newContent.song_title || ""}
                onChange={(e) => setNewContent({ ...newContent, song_title: e.target.value })}
                placeholder="Display name for this audio track (auto-filled from file)"
              />
            </div>

            {/* Audio File Selection */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Music className="h-4 w-4" />
                Audio File (optional - can add later)
              </Label>
              <Input
                value={newContent.file_url}
                onChange={(e) => setNewContent({ ...newContent, file_url: e.target.value })}
                placeholder="Paste file URL or select below"
              />
            </div>

            {/* Quick Select from Uploaded Files */}
            {uploadedFiles.filter(f => !f.linked_content).length > 0 && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FolderOpen className="h-4 w-4" />
                  Select from Uploaded Files
                </Label>
                <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
                  {uploadedFiles.filter(f => !f.linked_content).map(file => (
                    <button
                      key={file.filename}
                      type="button"
                      className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-muted transition-colors ${
                        newContent.file_url === file.file_url ? "bg-gold/20 text-gold" : ""
                      }`}
                      onClick={() => setNewContent({ 
                        ...newContent, 
                        file_url: file.file_url,
                        song_title: newContent.song_title || file.song_title || file.original_filename?.replace(/\.[^/.]+$/, "") || ""
                      })}
                    >
                      <div className="flex flex-col">
                        <span className="font-medium text-xs">{file.song_title || file.original_filename || file.filename}</span>
                        <span className="font-mono text-xs text-muted-foreground">{file.filename}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddContent} 
                disabled={saving || !newContent.title || !newContent.course_name}
                className="bg-gold text-primary-foreground"
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                Add to Library
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual customer-fulfillment download dialog */}
      <Dialog
        open={!!downloadDialog}
        onOpenChange={(open) => { if (!open) setDownloadDialog(null); }}
      >
        <DialogContent className="max-w-lg" data-testid="admin-download-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-purple-600" />
              Send a watermarked MP3 to a customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-md bg-purple-50 border border-purple-200 p-3 text-sm">
              <p className="text-purple-900">
                Song: <span className="font-semibold">{downloadDialog?.title}</span>
              </p>
              <p className="text-purple-700 text-xs mt-1">
                The customer&apos;s email will be baked into the MP3&apos;s ID3
                watermark, so leaked files are still traceable back to them.
              </p>
            </div>
            <div>
              <Label htmlFor="download-email">Customer email</Label>
              <Input
                id="download-email"
                type="email"
                placeholder="customer@example.com"
                value={downloadEmail}
                onChange={(e) => setDownloadEmail(e.target.value)}
                autoFocus
                data-testid="download-email-input"
              />
            </div>
            <div>
              <Label htmlFor="download-note">Order / context note (optional)</Label>
              <Input
                id="download-note"
                placeholder="e.g. manual send re: pack #12345"
                value={downloadNote}
                onChange={(e) => setDownloadNote(e.target.value)}
                data-testid="download-note-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Written into the MP3&apos;s ID3 comment for your records.
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setDownloadDialog(null)}
                disabled={downloading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAdminDownload}
                disabled={downloading || !downloadEmail.trim()}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="download-submit-btn"
              >
                {downloading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing…</>
                ) : (
                  <><Download className="mr-2 h-4 w-4" /> Download MP3</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <LeadSheetAdminDialog item={leadSheetItem} token={token} onClose={() => setLeadSheetItem(null)} onChanged={fetchData} />
    </div>
  );
}
